/**
 * Bileşen galerisi — görsel dilin tek sayfada görülebildiği yer.
 *
 * Hem geliştirme aracı hem de gözden geçirme yüzeyi: yeni bir bileşen
 * eklendiğinde buraya da eklenir, böylece tüm durumları (yükleniyor, hata,
 * devre dışı, boş) bir arada görülür. Ekranlar Görev 2'den itibaren gelecek.
 */
import { Badge, StatusDot } from './Status.tsx';
import { Button } from './Button.tsx';
import { Card, CardBody, CardHeader } from './Card.tsx';
import { EmptyState, SkeletonList } from './Feedback.tsx';
import { Field, Input, Select } from './Field.tsx';

function Section({
  title,
  note,
  children,
}: {
  readonly title: string;
  readonly note?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="text-lg font-semibold text-ink">{title}</h2>
        {note !== undefined && <p className="text-sm text-ink-muted">{note}</p>}
      </div>
      <div className="flex flex-col gap-3 rounded-[var(--radius-card)] border border-border bg-surface p-4">
        {children}
      </div>
    </section>
  );
}

export function Gallery(): React.JSX.Element {
  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-8 p-4 pb-16">
      <header>
        <h1 className="text-2xl font-bold text-ink">Tasarım sistemi</h1>
        <p className="text-sm text-ink-muted">
          Gasco SCADA v2 · mobil öncelikli · bileşenler bize ait [K-020]
        </p>
      </header>

      <Section title="Düğmeler" note="`md` ve `lg` en az 44 px — dokunma hedefi (WCAG 2.5.8).">
        <div className="flex flex-wrap gap-2">
          <Button>Kaydet</Button>
          <Button variant="secondary">Vazgeç</Button>
          <Button variant="ghost">Ayrıntı</Button>
          <Button variant="danger">Sil</Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm">Küçük</Button>
          <Button size="md">Orta</Button>
          <Button size="lg">Büyük</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button loading>Kaydediliyor</Button>
          <Button disabled>Devre dışı</Button>
        </div>
        <Button block size="lg">
          Mobilde tam genişlik
        </Button>
      </Section>

      <Section
        title="Durum göstergeleri"
        note="Renk TEK BAŞINA anlam taşımaz — her durum metin de taşır."
      >
        <div className="flex flex-wrap gap-2">
          <Badge tone="ok">Çevrimiçi</Badge>
          <Badge tone="warn">Düşük seviye</Badge>
          <Badge tone="danger">Yüksek basınç</Badge>
          <Badge tone="offline">Çevrimdışı</Badge>
          <Badge tone="neutral">Doğrulanmadı</Badge>
        </div>
        <p className="text-sm text-ink-muted">
          Çevrimdışı KASITLI olarak gri — kırmızı değil. Bilgi eksikliği bir alarm değildir; kırmızı
          yapmak gerçek alarmların dikkat değerini düşürür.
        </p>
        <div className="flex items-center gap-4">
          <StatusDot tone="ok" label="çevrimiçi" />
          <StatusDot tone="offline" label="çevrimdışı" />
          <StatusDot tone="danger" label="alarm" />
        </div>
      </Section>

      <Section
        title="Form kontrolleri"
        note="Etiket, hata ve yardım metni bileşenin içinde bağlanır."
      >
        <Field label="Saha adı" required hint="Müşterinin sahayı tanıdığı isim">
          {({ id, describedBy }) => (
            <Input id={id} aria-describedby={describedBy} placeholder="Limak Çimento — Tatvan" />
          )}
        </Field>
        <Field label="Pano IP adresi" error="Geçerli bir IPv4 adresi girin">
          {({ id, describedBy, invalid }) => (
            <Input id={id} aria-describedby={describedBy} invalid={invalid} defaultValue="188.38" />
          )}
        </Field>
        <Field label="PLC profili" hint="Adres haritası profilden gelir">
          {({ id, describedBy }) => (
            <Select id={id} aria-describedby={describedBy} defaultValue="kinco">
              <option value="kinco">Kinco</option>
              <option value="wago">Wago</option>
            </Select>
          )}
        </Field>
      </Section>

      <Section title="Kartlar" note="Mobilde tablo yerine kart — dikey akış, yatay kaydırma yok.">
        <Card interactive>
          <CardHeader>
            <div>
              <p className="font-medium text-ink">Limak Çimento — Tatvan</p>
              <p className="text-sm text-ink-muted">Bitlis · 1 tank</p>
            </div>
            <Badge tone="ok">Çevrimiçi</Badge>
          </CardHeader>
          <CardBody>
            <div className="flex gap-6 text-sm">
              <div>
                <p className="text-ink-muted">Seviye</p>
                <p className="tabular text-lg font-semibold">89,7%</p>
              </div>
              <div>
                <p className="text-ink-muted">Basınç</p>
                <p className="tabular text-lg font-semibold">3,19 bar</p>
              </div>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <div>
              <p className="font-medium text-ink">Savranlar Tarım</p>
              <p className="text-sm text-ink-muted">Aksaray · 2 tank</p>
            </div>
            <Badge tone="offline">7 gündür sessiz</Badge>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-ink-muted">Son başarılı okuma: 5 Ağustos 22:36</p>
          </CardBody>
        </Card>
      </Section>

      <Section title="Yükleniyor ve boş durumlar" note="Her listede tekrarlanır — bileşene alındı.">
        <SkeletonList rows={2} />
        <EmptyState
          title="Henüz saha yok"
          description="İlk sahayı ekleyerek başlayın."
          action={<Button>Saha ekle</Button>}
        />
      </Section>
    </main>
  );
}
